import { Insight } from '../../../common/hooks/use-insights-api.hook';
import { ConnectionKey } from '../../../common/hooks/use-home-api.hook';
import { AgentId } from './agents';
import { AgentTemplate, missingFor } from './templates';

/**
 * What Home puts in front of the vendor, decided without a model.
 *
 * Home used to be a wall of sentences. It is now three things: a place to ask,
 * a few counts of what is waiting, and three suggestions — each owned by the
 * agent whose area it is, led by the one figure it turns on, and ending in a
 * button that hands it to the team. Every one comes from a finding the engine
 * already computed; this only chooses and shapes.
 */

/** Which agent a finding belongs to — whose area it is, and who picks it up. */
export function suggestionOwner(finding: Pick<Insight, 'kind' | 'metric'>): AgentId {
    switch (finding.kind) {
        case 'seo_shift':
        case 'analysis':
            return 'market';
        case 'catalogue_gap':
            return 'catalogue';
        case 'content_gap':
            return 'marketing';
        case 'setup_gap':
            return 'lead';
        default:
            break;
    }
    const metric = finding.metric ?? '';
    if (/lead/.test(metric)) return 'sales';
    if (/email|social|post|asset|generation|credit/.test(metric)) return 'marketing';
    if (/ad_|ads_|spend/.test(metric)) return 'ads';
    return 'analyst';
}

/**
 * The figure a card leads with: the movement, as a signed percentage.
 *
 * Only when there is one. A setup gap has no number, and inventing a figure to
 * fill the space is the kind of plausible decoration that makes a real figure
 * worth less.
 */
export function suggestionFigure(finding: Pick<Insight, 'change'>): string | null {
    const change = finding.change;
    if (change == null || !Number.isFinite(change) || change === 0) return null;
    const rounded = Math.abs(change) >= 10 ? Math.round(Math.abs(change)) : Math.round(Math.abs(change) * 10) / 10;
    return `${change > 0 ? '+' : '−'}${rounded}%`;
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

/**
 * The few findings worth a card: vendor-facing, still open, most severe first,
 * then by the engine's own score, at most one per kind+metric so three cards
 * are not three views of the same drop.
 */
export function pickSuggestions(items: readonly Insight[], limit = 3): Insight[] {
    const seen = new Set<string>();
    return [...items]
        .filter((i) => i.status === 'open')
        .sort((a, b) =>
            (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3) || (b.score ?? 0) - (a.score ?? 0))
        .filter((i) => {
            const key = `${i.kind}:${i.metric}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .slice(0, limit);
}

export type HomeCard =
    | { kind: 'finding'; finding: Insight }
    | { kind: 'template'; template: AgentTemplate };

/**
 * Home's one row: what the team noticed, and something it can do.
 *
 * Three cards, so Home fits on one screen. At most two are findings — the most
 * urgent — so there is always at least one ready-made agent beside them: a
 * vendor with nothing wrong still has something to start. Templates whose
 * connections are missing are skipped here (they would start with "connect X
 * first"); the full gallery still shows them.
 */
export function homeRow(
    findings: readonly Insight[],
    templates: readonly AgentTemplate[],
    connections: ReadonlyArray<{ key: ConnectionKey; state: string }> | null,
    size = 3,
): HomeCard[] {
    const cards: HomeCard[] = findings.slice(0, size - 1).map((finding) => ({ kind: 'finding' as const, finding }));
    for (const template of templates) {
        if (cards.length >= size) break;
        if (missingFor(template, connections).length) continue;
        cards.push({ kind: 'template', template });
    }
    return cards;
}

/**
 * The brief that hands a suggestion to the team.
 *
 * Written as the vendor would ask it, with the finding's own words as the
 * evidence — the lead then decides who to send, and the vendor watches them
 * go. Localised text in, so the conversation starts in the vendor's language.
 */
export const suggestionBrief = (title: string, detail: string, ask: string): string =>
    `${ask}\n\n"${title}"${detail ? ` — ${detail}` : ''}`;

export interface HomeChip {
    readonly id: 'approve' | 'attention' | 'leads' | 'working';
    readonly count: number;
}

/** The counts under the composer. A zero is not shown — it is not waiting. */
export function homeChips(input: {
    approvals: number;
    attention: number;
    leads: number | null;
    working: number;
}): HomeChip[] {
    const chips: HomeChip[] = [
        { id: 'working', count: input.working },
        { id: 'approve', count: input.approvals },
        { id: 'attention', count: input.attention },
        { id: 'leads', count: input.leads ?? 0 },
    ];
    return chips.filter((c) => c.count > 0);
}

/** "Good morning" by the vendor's own clock. */
export function greetingKey(now: Date = new Date()): 'MORNING' | 'AFTERNOON' | 'EVENING' {
    const h = now.getHours();
    // Past midnight is still the evening to anyone awake; morning starts at five.
    if (h < 5) return 'EVENING';
    if (h < 12) return 'MORNING';
    if (h < 18) return 'AFTERNOON';
    return 'EVENING';
}
